import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ImagePlus, Play, Share2, Trash2, Pencil, Check, X, Copy, Sparkles, Music, Upload, Mic, Wand2, FileEdit, Film, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Spinner } from "@/components/Bits";
import { VNPlayer } from "@/components/VNPlayer";
import VNEditor from "@/pages/VNEditor";
import { toast } from "sonner";

const asset = (u) => (!u ? null : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export default function VNScene() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scene, setScene] = useState(null);
  const [err, setErr] = useState("");
  const [playing, setPlaying] = useState(false);
  const [busyBg, setBusyBg] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const pollRef = useRef(null);
  const voicePollRef = useRef(null);
  const fileRef = useRef(null);
  const [showEditor, setShowEditor] = useState(false);
  const [musicBusy, setMusicBusy] = useState(false);
  const [voicesBusy, setVoicesBusy] = useState(false);
  const [trailerBusy, setTrailerBusy] = useState(false);

  const load = () => api.get(`/vn/scenes/${id}`).then(({ data }) => { setScene(data); return data; })
    .catch((e) => { setErr(e.response?.data?.detail || "Scene not found."); });

  useEffect(() => { load(); return () => { clearInterval(pollRef.current); clearInterval(voicePollRef.current); }; /* eslint-disable-next-line */ }, [id]);

  const bgTotal = scene ? Object.keys(scene.backgrounds || {}).length : 0;
  const bgDone = scene ? Object.keys(scene.bg_urls || {}).length : 0;

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await load();
      if (!d || ["ready", "error", "partial"].includes(d.bg_status)) {
        clearInterval(pollRef.current);
        setBusyBg(false);
        if (d && d.bg_status === "ready") toast.success("Backgrounds are ready! 🎨");
        if (d && d.bg_status === "partial") toast("Some backgrounds painted — tap again to finish the rest.");
        if (d && d.bg_status === "error") toast.error("Backgrounds failed — try again.");
      }
    }, 4000);
  };

  const genBackgrounds = async () => {
    setBusyBg(true);
    try {
      await api.post(`/vn/scenes/${id}/backgrounds`);
      toast("Painting backgrounds… this takes a moment.");
      setScene((s) => ({ ...s, bg_status: "building" }));
      startPolling();
    } catch (e) { setBusyBg(false); toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start."); }
  };

  const share = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/vn/scenes/${id}/share`);
      setScene((s) => ({ ...s, share_code: data.code }));
      const link = `${window.location.origin}/vn/${data.code}`;
      try { await navigator.clipboard.writeText(link); toast.success("Share link copied! 🔗"); }
      catch { toast.success("Scene shared!"); }
    } catch { toast.error("Couldn't share."); } finally { setSharing(false); }
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/vn/${scene.share_code}`;
    try { await navigator.clipboard.writeText(link); toast.success("Link copied!"); } catch { /* ignore */ }
  };

  const saveTitle = async () => {
    const t = newTitle.trim();
    if (!t) { setEditing(false); return; }
    try { await api.patch(`/vn/scenes/${id}`, { title: t }); setScene((s) => ({ ...s, title: t })); setEditing(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't rename."); }
  };

  const del = async () => {
    if (!window.confirm("Delete this scene? This can't be undone.")) return;
    try { await api.delete(`/vn/scenes/${id}`); toast.success("Scene deleted."); navigate("/app/vn"); }
    catch { toast.error("Couldn't delete."); }
  };

  const genMusic = async () => {
    setMusicBusy(true);
    try { const { data } = await api.post(`/vn/scenes/${id}/music/generate`, {}); setScene((s) => ({ ...s, music: data.music })); toast.success("AI music added! 🎵"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't compose music."); }
    finally { setMusicBusy(false); }
  };
  const uploadMusic = async (file) => {
    if (!file) return;
    if (file.size > 8_000_000) { toast.error("Track too large — keep it under 8 MB."); return; }
    setMusicBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const { data } = await api.post(`/vn/scenes/${id}/music/upload`, { data: dataUrl, name: file.name.slice(0, 60) });
      setScene((s) => ({ ...s, music: data.music })); toast.success("Track uploaded! 🎶");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't upload that track."); }
    finally { setMusicBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeMusic = async () => {
    try { await api.delete(`/vn/scenes/${id}/music`); setScene((s) => ({ ...s, music: null })); toast("Music removed."); }
    catch { toast.error("Couldn't remove."); }
  };
  const genVoices = async () => {
    setVoicesBusy(true);
    try {
      await api.post(`/vn/scenes/${id}/voices`);
      toast("Recording AI voices… this takes a moment.");
      setScene((s) => ({ ...s, voice_generating: true }));
      clearInterval(voicePollRef.current);
      voicePollRef.current = setInterval(async () => {
        const d = await load();
        const st = d && d.voice_status;
        if (!d || (!d.voice_generating && st && ["done", "failed"].includes(st.status))) {
          clearInterval(voicePollRef.current); setVoicesBusy(false);
          if (st && st.status === "done") toast.success(`Voiced ${st.created} lines! 🎙️`);
          if (st && st.status === "failed") toast.error("Voice generation failed — try again.");
        }
      }, 4000);
    } catch (e) { setVoicesBusy(false); toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start voices."); }
  };
  const makeTrailer = async () => {
    if (!Object.keys(scene.bg_urls || {}).length) { toast.error("Generate AI backgrounds first to make a trailer."); return; }
    setTrailerBusy(true);
    try { const { data } = await api.post(`/vn/scenes/${id}/trailer`); setScene((s) => ({ ...s, trailer_url: data.url })); toast.success("Trailer ready! 🎬"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't build the trailer."); }
    finally { setTrailerBusy(false); }
  };

  if (err) return <div className="min-h-[60vh] grid place-items-center text-center"><div><p className="mb-3">{err}</p><button onClick={() => navigate("/app/vn")} className="text-[hsl(var(--primary))] font-semibold">Back to VN Studio</button></div></div>;
  if (!scene) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="vn-scene-page">
      <button onClick={() => navigate("/app/vn")} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-4" data-testid="vn-back">
        <ArrowLeft className="w-4 h-4" /> VN Studio
      </button>

      <div className="flex items-start justify-between gap-3 mb-2">
        {editing ? (
          <div className="flex items-center gap-2 flex-1">
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="vn-title-edit"
              className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-lg font-bold" autoFocus />
            <button onClick={saveTitle} data-testid="vn-title-save" className="w-9 h-9 grid place-items-center rounded-full bg-[hsl(var(--primary))] text-white"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEditing(false)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--secondary))]"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <h1 className="font-display text-3xl font-extrabold" data-testid="vn-scene-title">{scene.title}</h1>
            <button onClick={() => { setNewTitle(scene.title); setEditing(true); }} data-testid="vn-rename-btn" className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--secondary))]"><Pencil className="w-4 h-4" /></button>
          </div>
        )}
        <button onClick={del} data-testid="vn-delete-btn" className="w-9 h-9 grid place-items-center rounded-full hover:bg-rose-500/10 text-rose-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{scene.premise}</p>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{scene.node_count} beats · {scene.ending_count} ending{scene.ending_count === 1 ? "" : "s"} · {scene.depth === "branching" ? "branching" : "light"} · {scene.plays || 0} plays</p>

      {/* Cast row */}
      {(scene.cast || []).length > 0 && (
        <div className="flex flex-wrap gap-3 mb-5" data-testid="vn-scene-cast">
          {scene.cast.map((c) => (
            <div key={c.char_id} className="flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] pr-3">
              <span className="w-9 h-9 rounded-full overflow-hidden bg-[hsl(var(--secondary))] grid place-items-center">
                {c.image ? <img src={asset(c.image)} alt={c.name} className="w-full h-full object-cover" /> : <span>{c.emoji}</span>}
              </span>
              <span className="text-xs font-semibold">{c.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={() => setPlaying((v) => !v)} data-testid="vn-play-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90">
          <Play className="w-4 h-4" /> {playing ? "Close player" : "Play scene"}
        </button>

        {scene.bg_status === "ready" ? (
          <button onClick={genBackgrounds} disabled={busyBg} data-testid="vn-regen-bg-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2.5 text-sm font-semibold hover:bg-[hsl(var(--secondary))]">
            <ImagePlus className="w-4 h-4" /> Backgrounds ready ✓
          </button>
        ) : (
          <button onClick={genBackgrounds} disabled={busyBg || scene.bg_status === "building"} data-testid="vn-gen-bg-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2.5 text-sm font-semibold hover:bg-[hsl(var(--secondary))] disabled:opacity-60">
            {(busyBg || scene.bg_status === "building") ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            {(busyBg || scene.bg_status === "building") ? `Painting ${bgDone}/${bgTotal}…` : `Generate AI backgrounds (${bgTotal})`}
          </button>
        )}

        {scene.share_code ? (
          <button onClick={copyLink} data-testid="vn-copy-link-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] px-4 py-2.5 text-sm font-semibold hover:bg-[hsl(var(--primary))]/5">
            <Copy className="w-4 h-4" /> Copy share link
          </button>
        ) : (
          <button onClick={share} disabled={sharing} data-testid="vn-share-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2.5 text-sm font-semibold hover:bg-[hsl(var(--secondary))]">
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share
          </button>
        )}
      </div>

      {scene.bg_status !== "ready" && !playing && (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-4 mb-6 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2" data-testid="vn-bg-hint">
          <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" /> Generate AI backgrounds to bring each location to life. You can still play now — scenes use a themed backdrop until then.
        </div>
      )}

      {/* Sound & Voices */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 mb-6" data-testid="vn-sound-panel">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5"><Music className="w-4 h-4 text-[hsl(var(--primary))]" /> Sound & voices</h3>
        <div className="flex flex-wrap items-center gap-2">
          {scene.music ? (
            <div className="flex items-center gap-2 rounded-full border border-[hsl(var(--border))] pl-3 pr-1 py-1" data-testid="vn-music-current">
              <span className="text-xs font-semibold max-w-[140px] truncate">{scene.music.name}</span>
              <audio src={asset(scene.music.url)} controls className="h-8" data-testid="vn-music-audio" />
              <button onClick={removeMusic} data-testid="vn-music-remove" className="w-7 h-7 grid place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <>
              <button onClick={genMusic} disabled={musicBusy} data-testid="vn-gen-music-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--secondary))] disabled:opacity-50">
                {musicBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {musicBusy ? "Composing…" : "Generate AI music"}
              </button>
              <button onClick={() => fileRef.current?.click()} disabled={musicBusy} data-testid="vn-upload-music-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--secondary))] disabled:opacity-50">
                <Upload className="w-4 h-4" /> Upload track
              </button>
              <input ref={fileRef} type="file" accept="audio/*" className="hidden" data-testid="vn-music-file" onChange={(e) => uploadMusic(e.target.files?.[0])} />
            </>
          )}

          <button onClick={genVoices} disabled={voicesBusy || scene.voice_generating}
            data-testid={scene.voiced ? "vn-revoice-btn" : "vn-gen-voices-btn"}
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--secondary))] disabled:opacity-50">
            {(voicesBusy || scene.voice_generating) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
            {(voicesBusy || scene.voice_generating) ? "Recording voices…" : scene.voiced ? "Voices added ✓ · Redo" : "Add AI voices"}
          </button>

          <button onClick={() => setShowEditor((v) => !v)} data-testid="vn-edit-script-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--secondary))]">
            <FileEdit className="w-4 h-4" /> {showEditor ? "Close editor" : "Edit script"}
          </button>

          <button onClick={makeTrailer} disabled={trailerBusy} data-testid="vn-trailer-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--secondary))] disabled:opacity-50">
            {trailerBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />} {trailerBusy ? "Filming…" : scene.trailer_url ? "Rebuild trailer" : "Make trailer"}
          </button>
        </div>
      </div>

      {scene.trailer_url && (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 mb-6" data-testid="vn-trailer-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-1.5"><Film className="w-4 h-4 text-[hsl(var(--primary))]" /> Shareable trailer</h3>
            <a href={asset(scene.trailer_url)} download data-testid="vn-trailer-download"
              className="text-xs font-semibold text-[hsl(var(--primary))] inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Download GIF</a>
          </div>
          <img src={asset(scene.trailer_url)} alt="Scene trailer" data-testid="vn-trailer-gif" className="w-full max-w-lg rounded-xl border border-[hsl(var(--border))]" />
        </div>
      )}

      {showEditor && (
        <div className="mb-6" data-testid="vn-editor-wrap">
          <VNEditor scene={scene} onSaved={(s) => { setScene(s); toast.success("Scene updated."); }} />
        </div>
      )}

      {/* Inline player */}
      {playing && (
        <div className="mb-6" data-testid="vn-inline-player">
          <VNPlayer scene={scene} code={scene.share_code || ""} canSave={!!scene.share_code} className="min-h-[70vh]" />
        </div>
      )}
    </div>
  );
}
